import React, { type SyntheticEvent } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Checkbox from '@atlaskit/checkbox';
import { Label } from '@atlaskit/form';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssignee$key } from '@atlassian/jira-relay/src/__generated__/assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssignee.graphql';
import type { assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssigneeFieldMutation } from '@atlassian/jira-relay/src/__generated__/assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssigneeFieldMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import messages from './messages.tsx';

export const NotifyWhenRoleIsAssignee = ({
	assigneeRolePreferenceFragment,
}: {
	assigneeRolePreferenceFragment: assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssignee$key;
}) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = useFragment(
		graphql`
			fragment assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssignee on JiraNotificationOptions {
				id @required(action: THROW)
				notifyWhenRoleAssigneeEnabled
			}
		`,
		assigneeRolePreferenceFragment,
	);

	const [commit] =
		useMutation<assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssigneeFieldMutation>(
			graphql`
				mutation assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssigneeFieldMutation(
					$cloudId: ID!
					$input: JiraUpdateNotificationOptionsInput!
				) {
					jira {
						updateGlobalNotificationOptions(cloudId: $cloudId, input: $input)
							@optIn(to: "JiraNotificationPreference") {
							success
							errors {
								message
							}
							options {
								...assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssignee
							}
						}
					}
				}
			`,
		);

	const handleOnChange = (option: SyntheticEvent<HTMLInputElement>) => {
		if (option) {
			commit({
				variables: {
					cloudId,
					input: {
						notifyWhenRoleAssigneeEnabled: option.currentTarget.checked,
					},
				},
				optimisticResponse: {
					jira: {
						updateGlobalNotificationOptions: {
							success: true,
							errors: [],
							options: {
								id: data.id,
								notifyWhenRoleAssigneeEnabled: option.currentTarget.checked,
							},
						},
					},
				},
				onError: (error) => {
					showErrorFlag();
					fireErrorAnalytics({
						meta: {
							id: 'mutateNotifyWhenRoleAssigneeEnabled',

							packageName: 'jiraPersonalSettingsPreferenceFields',
							teamName: 'jira-notifications-platform',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
				onCompleted: (response) => {
					const errors = response?.jira?.updateGlobalNotificationOptions?.errors;
					if (errors && errors?.length > 0) {
						showErrorFlag();
						errors.forEach((error) => {
							fireErrorAnalytics({
								meta: {
									id: 'mutateNotifyWhenRoleAssigneeEnabledPreferenceCompletedError',
									packageName: 'jiraPersonalSettingsPreferenceFields',
									teamName: 'jira-notifications-platform',
								},
								error: new Error(error?.message || 'Unknown Error'),
								sendToPrivacyUnsafeSplunk: true,
							});
						});
					} else {
						fireOperationalAnalytics(
							createAnalyticsEvent({}),
							'mutateNotifyWhenRoleAssigneeEnabledPreference success',
						);
					}
				},
			});
		}
	};
	return (
		<Label htmlFor="notify-when-role-assignee-enabled-checkbox">
			<Inline alignBlock="center">
				<Checkbox
					id="notify-when-role-assignee-enabled-checkbox"
					isChecked={!!data.notifyWhenRoleAssigneeEnabled}
					onChange={handleOnChange}
					testId="personal-settings-preference-fields.common.ui.assignee-role.checkbox"
				/>
				<Box
					paddingInlineStart="space.100"
					testId="personal-settings-preference-fields.common.ui.assignee-role.toggle"
					xcss={labelStyles}
				>
					{formatMessage(messages.checkboxLabel)}
				</Box>
			</Inline>
		</Label>
	);
};

const labelStyles = xcss({
	font: token('font.body'),
	marginRight: 'space.100',
});
